export type PipUnavailableReason = 'insecure-context' | 'embedded' | 'unsupported'
export type PipWidgetMode = 'pip' | 'popup'
export type PipIssueCode = PipUnavailableReason | 'not-connected' | 'ocr-preparing' | 'ocr-error'
  | 'ending' | 'reloading' | 'request-denied' | 'resource-load' | 'unknown'
export type PipFailureStage = 'availability' | 'prerequisite' | 'request' | 'resources' | 'initialization'

export interface PipEnvironment {
  secureContext: boolean
  topLevel: boolean
  requestWindow: boolean
  openPopup: boolean
}

export interface PipAvailability {
  supported: boolean
  mode: PipWidgetMode | null
  reason: PipUnavailableReason | null
  environment: PipEnvironment
}

let documentPipAbandoned = false

export function resetDocumentPipAvailability(): void {
  documentPipAbandoned = false
}

export function abandonDocumentPip(): void {
  documentPipAbandoned = true
}

export function isAbandonedDocumentPipError(error: PipOpenError): boolean {
  return error.stage === 'request' && ['InvalidStateError', 'NotSupportedError', 'TypeError'].includes(error.errorName)
}

export function defaultPipEnvironment(): PipEnvironment {
  const requestWindow = typeof window !== 'undefined' && typeof window.documentPictureInPicture?.requestWindow === 'function'
  return {
    secureContext: typeof window !== 'undefined' && window.isSecureContext === true,
    topLevel: typeof window !== 'undefined' && window.top === window,
    requestWindow: requestWindow && !documentPipAbandoned,
    openPopup: typeof window !== 'undefined' && typeof window.open === 'function',
  }
}

export function getPipAvailability(environment: PipEnvironment = defaultPipEnvironment()): PipAvailability {
  const reason = !environment.secureContext ? 'insecure-context'
    : !environment.topLevel ? 'embedded'
      : !environment.requestWindow && !environment.openPopup ? 'unsupported' : null
  const mode = reason ? null : environment.requestWindow ? 'pip' : 'popup'
  return { supported: reason === null, mode, reason, environment }
}

export class PipOpenError extends Error {
  constructor(readonly code: PipIssueCode, readonly stage: PipFailureStage, readonly errorName = 'Error') {
    super(code)
    this.name = 'PipOpenError'
  }
}

export function classifyPipError(error: unknown, stage: PipFailureStage): PipOpenError {
  if (error instanceof PipOpenError) return error
  // Keep only known error names, never arbitrary exception text or resource URLs.
  const name = error && typeof error === 'object' && 'name' in error ? error.name : null
  const errorName = typeof name === 'string' && [
    'NotAllowedError', 'SecurityError', 'NotSupportedError', 'InvalidStateError',
    'AbortError', 'TypeError', 'Error',
  ].includes(name) ? name : 'UnknownError'
  const brokenPip = stage === 'request' && ['InvalidStateError', 'NotSupportedError', 'TypeError'].includes(errorName)
  const denied = stage === 'request' && ['NotAllowedError', 'SecurityError', 'InvalidStateError'].includes(errorName)
  return new PipOpenError(stage === 'resources' ? 'resource-load' : denied || brokenPip ? 'request-denied' : 'unknown', stage, errorName)
}

export interface PipIssue {
  code: PipIssueCode
  stage: PipFailureStage
  diagnostic: {
    at: string
    userAgent: string
    availability: PipAvailability
    stage: PipFailureStage
    code: PipIssueCode
    errorName: string
    widgetMode: PipWidgetMode | null
    hasDocumentPip: boolean
    documentPipAbandoned: boolean
  }
}

export function createPipIssue(error: PipOpenError, availability = getPipAvailability()): PipIssue {
  return {
    code: error.code, stage: error.stage,
    diagnostic: {
      at: new Date().toISOString(), userAgent: navigator.userAgent,
      availability, stage: error.stage, code: error.code, errorName: error.errorName,
      widgetMode: availability.mode, hasDocumentPip: availability.environment.requestWindow,
      documentPipAbandoned,
    },
  }
}

export type PipOpenResult = { status: 'opened' | 'cancelled' } | { status: 'failed'; issue: PipIssue }
